import { useEffect, useRef, useState } from "react";
import { Send, User, Cpu, AlertTriangle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAppConfig } from "@/context/ConfigContext";
import { PageHeader } from "@/pages/Dashboard";
import { sendChat } from "@/lib/api";
import { getEnvironment, agentChat } from "@/lib/agent";
import { cn } from "@/lib/utils";

export default function Chat() {
  const { models, config } = useAppConfig();
  const chatModels = models.filter((m) => (m.capabilities || []).includes("chat"));
  const [model, setModel] = useState("");
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(512);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const idRef = useRef(0);
  const nextId = () => (idRef.current += 1);

  useEffect(() => {
    if (config) {
      setTemperature(config.default_temperature);
      setMaxTokens(config.default_max_tokens);
    }
  }, [config]);

  useEffect(() => {
    if (!model && chatModels.length > 0) {
      setModel(config?.default_model && chatModels.some((m) => m.id === config.default_model) ? config.default_model : chatModels[0].id);
    }
  }, [chatModels, model, config]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!input.trim() || !model || loading) return;
    const userMsg = { id: nextId(), role: "user", content: input.trim() };
    const history = [...messages, userMsg];
    setMessages(history);
    setInput("");
    setLoading(true);

    const payload = {
      model,
      messages: history.map((m) => ({ role: m.role, content: m.content })),
      temperature,
      max_tokens: maxTokens,
    };
    // localhost -> backend local (Mac) ; preview/publicada -> puente del agente (8765)
    const env = getEnvironment();
    const request = env.mode === "local" ? sendChat(payload) : agentChat(payload);
    const data = await request.catch((e) => ({
      ok: false,
      status: "ERROR",
      error: e?.message || "Error de red",
    }));

    if (data.ok) {
      setMessages((prev) => [
        ...prev,
        {
          id: nextId(),
          role: "assistant",
          content: data.content,
          meta: { model: data.model, ms: data.response_time_ms },
        },
      ]);
    } else {
      setMessages((prev) => [...prev, { id: nextId(), role: "error", content: data.error, status: data.status }]);
    }
    setLoading(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="krai-enter">
      <PageHeader title="Chat" subtitle="Prueba de conversación con el modelo local" />

      <div className="flex flex-col gap-4 lg:flex-row">
        {/* Chat stream */}
        <div className="flex min-h-[60vh] flex-1 flex-col rounded-md border border-[#262626] bg-[#121212]">
          <div className="flex items-center justify-between border-b border-[#262626] px-4 py-2.5">
            <span className="font-mono text-[11px] uppercase tracking-widest text-[#737373]">Conversación</span>
            <button
              data-testid="chat-clear"
              onClick={() => setMessages([])}
              className="flex items-center gap-1.5 rounded-sm px-2 py-1 font-mono text-[11px] uppercase tracking-wider text-[#737373] transition-colors hover:text-white"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Limpiar
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-4" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="flex h-full min-h-[40vh] items-center justify-center text-center">
                <p className="max-w-sm text-sm text-[#737373]">
                  Selecciona un modelo detectado y escribe un mensaje para probar el servidor local.
                </p>
              </div>
            )}
            {messages.map((m, i) => (
              <MessageBubble key={m.id} message={m} index={i} />
            ))}
            {loading && (
              <div className="flex items-center gap-2 font-mono text-xs text-[#737373]">
                <Cpu className="h-4 w-4 animate-pulse" /> generando…
              </div>
            )}
          </div>

          <div className="border-t border-[#262626] p-3">
            <div className="flex items-end gap-2">
              <Textarea
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={model ? "Escribe un mensaje…" : "No hay modelos detectados"}
                disabled={!model || loading}
                className="min-h-[44px] resize-none border-[#262626] bg-[#0a0a0a] text-sm focus-visible:ring-1 focus-visible:ring-neutral-500"
                rows={1}
              />
              <Button
                data-testid="chat-send"
                onClick={send}
                disabled={!input.trim() || !model || loading}
                className="h-11 rounded-sm bg-white text-black hover:bg-neutral-200"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Settings panel */}
        <div className="w-full shrink-0 space-y-4 lg:w-80">
          <div className="rounded-md border border-[#262626] bg-[#121212] p-4">
            <span className="font-mono text-[11px] uppercase tracking-widest text-[#737373]">Parámetros</span>

            <div className="mt-4 space-y-1.5">
              <label className="font-mono text-xs uppercase tracking-wider text-[#a3a3a3]">Modelo</label>
              <Select value={model} onValueChange={setModel} disabled={chatModels.length === 0}>
                <SelectTrigger
                  data-testid="chat-model-select"
                  className="border-[#262626] bg-[#0a0a0a] font-mono text-xs"
                >
                  <SelectValue placeholder="Sin modelos" />
                </SelectTrigger>
                <SelectContent className="border-[#262626] bg-[#121212]">
                  {chatModels.map((m) => (
                    <SelectItem key={m.id} value={m.id} className="font-mono text-xs">
                      {m.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between">
                <label className="font-mono text-xs uppercase tracking-wider text-[#a3a3a3]">Temperatura</label>
                <span className="font-mono text-xs text-white" data-testid="chat-temp-value">{temperature.toFixed(2)}</span>
              </div>
              <Slider
                data-testid="chat-temperature"
                value={[temperature]}
                min={0}
                max={2}
                step={0.05}
                onValueChange={(v) => setTemperature(v[0])}
              />
            </div>

            <div className="mt-4 space-y-1.5">
              <label className="font-mono text-xs uppercase tracking-wider text-[#a3a3a3]">Max tokens</label>
              <Input
                data-testid="chat-max-tokens"
                type="number"
                min={1}
                value={maxTokens}
                onChange={(e) => setMaxTokens(Number(e.target.value))}
                className="border-[#262626] bg-[#0a0a0a] font-mono text-xs focus-visible:ring-1 focus-visible:ring-neutral-500"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ message, index }) {
  if (message.role === "error") {
    return (
      <div data-testid={`chat-message-error-${index}`} className="flex items-start gap-3 rounded-md border border-red-500/40 bg-red-500/10 p-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-red-400">Error {message.status}</div>
          <div className="mt-1 break-all font-mono text-sm text-red-200">{message.content}</div>
        </div>
      </div>
    );
  }

  const isUser = message.role === "user";
  return (
    <div className={cn("flex gap-3", isUser ? "flex-row-reverse" : "flex-row")} data-testid={`chat-message-${index}`}>
      <div
        className={cn(
          "flex h-7 w-7 shrink-0 items-center justify-center rounded-sm border",
          isUser ? "border-[#404040] bg-[#1a1a1a]" : "border-emerald-500/30 bg-emerald-500/10"
        )}
      >
        {isUser ? <User className="h-3.5 w-3.5 text-[#a3a3a3]" /> : <Cpu className="h-3.5 w-3.5 text-emerald-400" />}
      </div>
      <div className={cn("max-w-[80%]", isUser ? "text-right" : "text-left")}>
        <div
          className={cn(
            "inline-block whitespace-pre-wrap rounded-md border px-3 py-2 text-sm",
            isUser ? "border-[#404040] bg-[#1a1a1a] text-[#f5f5f5]" : "border-[#262626] bg-[#0a0a0a] text-[#e5e5e5]"
          )}
        >
          {message.content}
        </div>
        {message.meta && (
          <div className="mt-1 font-mono text-[10px] text-[#737373]">
            {message.meta.model} · {message.meta.ms} ms
          </div>
        )}
      </div>
    </div>
  );
}
